import { Text, View } from '@react-pdf/renderer'
import { ComponentType, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { commonPdfTableStyles } from '@features/exports/utils/commonTableStyles'
import { ReportsType } from '@features/reports/enums/reportsType'
import { ReportRowType } from '@features/reports/types/reportRowType'
import { GenericTableRow } from '@features/reports/utils/buildTableData'

import { columnPdfStyles } from '../../../../utils/columnPdfStyles'
import { getRowByTypeStyles } from '../../utils/getRowByTypeStyles'
import { ReportsPdfTableRow } from '../ReportsPdfTableRow'

interface TableRowsRendererProps<T> {
  rows: GenericTableRow<T>[]
  BalanceCell: ComponentType<{ row: GenericTableRow<T> }>
  reportType: ReportsType
}

const getLabelTextStyle = (type: ReportRowType) => {
  switch (type) {
    case ReportRowType.Header:
    case ReportRowType.Footer:
      return commonPdfTableStyles.headerText
    case ReportRowType.Group:
      return commonPdfTableStyles.subHeaderText
    default:
      return commonPdfTableStyles.bodyText
  }
}

export const TableRowsRenderer = <T,>({ rows, BalanceCell, reportType }: TableRowsRendererProps<T>) => {
  const { t } = useTranslation()

  const isFollowedByContentRow = useCallback(
    (row: GenericTableRow<T>, index: number) => {
      if (row.type !== ReportRowType.Group) {
        return false
      }

      const nextRow = rows[index + 1]
      return nextRow && (nextRow.type === ReportRowType.Account || nextRow.type === ReportRowType.Group)
    },
    [rows],
  )

  return (
    <>
      {rows.map((row, index) => {
        const { id, label, type } = row

        const isLastItem = index === rows.length - 1
        const shouldApplyBottomMargin = isFollowedByContentRow(row, index)
        const isHeaderType = type === ReportRowType.Header
        const isFooterType = type === ReportRowType.Footer
        const isGroupType = type === ReportRowType.Group
        const shouldUppercaseLabel = isHeaderType || isGroupType || isFooterType

        return (
          <ReportsPdfTableRow key={id} customStyles={getRowByTypeStyles(type, shouldApplyBottomMargin, isLastItem)}>
            <View style={columnPdfStyles.columnFixed} wrap={false}>
              <Text style={getLabelTextStyle(type)}>
                {isGroupType && reportType === ReportsType.ProfitAndLoss && `${t('total').toUpperCase()} `}
                {shouldUppercaseLabel ? label.toUpperCase() : label}
              </Text>
            </View>

            <BalanceCell row={row} />
          </ReportsPdfTableRow>
        )
      })}
    </>
  )
}
